import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCalendar,
  FiCheck,
  FiGlobe,
  FiPhone,
  FiUsers,
} from 'react-icons/fi';

import { CRISIS_VALUE, QUESTIONS, buildResult, isComplete } from './quizData';

const TITLE_ID = 'mm-quiz-title';

// Two of the four helplines on /crisis, chosen because both are free and answer
// around the clock. The full list is one tap away rather than reproduced here —
// this is an interruption, not a directory.
const HELPLINES = [
  { name: 'Tele-MANAS', number: '14416', href: 'tel:14416' },
  { name: 'AASRA', number: '+91 9820466726', href: 'tel:+919820466726' },
];

/**
 * Shown the moment someone says they have thoughts of harming themselves,
 * before the quiz goes any further.
 *
 * It does not block them — being told "you cannot continue" by a website is
 * its own small cruelty, and they may well be filling this in for exactly the
 * right reason. It just refuses to let a matching questionnaire be the next
 * thing they see without the numbers being offered first.
 */
function CrisisStep({ onContinue }) {
  return (
    <div className="mm-quiz__crisis">
      <h3 className="mm-quiz__crisis-title">
        Before anything else &mdash; you can talk to someone right now.
      </h3>
      <p className="mm-quiz__crisis-text">
        Thank you for telling us. Finding a therapist takes days, and what
        you&rsquo;ve described deserves someone tonight. These lines are free,
        confidential, and answered by trained people around the clock.
      </p>

      <ul className="mm-quiz__helplines">
        {HELPLINES.map(({ name, number, href }) => (
          <li key={name}>
            <a href={href}>
              <FiPhone size={15} aria-hidden="true" />
              <span className="mm-quiz__helpline-name">{name}</span>
              <span className="mm-quiz__helpline-number">{number}</span>
            </a>
          </li>
        ))}
      </ul>

      <div className="mm-quiz__crisis-actions">
        <Button as={Link} to="/crisis" className="mm-quiz__crisis-cta">
          More ways to get help now
        </Button>
        <Button variant="link" className="mm-quiz__skip" onClick={onContinue}>
          Continue the quiz
        </Button>
      </div>
    </div>
  );
}

function Result({ result, onSend, onBrowse }) {
  return (
    <div className="mm-quiz__result">
      <span className="mm-quiz__result-icon" aria-hidden="true">
        <FiCheck size={26} />
      </span>

      <h3 className="mm-quiz__result-title" id={TITLE_ID}>
        Here&rsquo;s where we&rsquo;d start
      </h3>

      {result.condition ? (
        <p className="mm-quiz__result-text">
          A therapist who works with <strong>{result.condition.toLowerCase()}</strong>
          {result.also.length > 0 && (
            <>
              {' '}
              and understands {result.also.map((v) => v.toLowerCase()).join(' and ')}
            </>
          )}
          .
        </p>
      ) : (
        <p className="mm-quiz__result-text">
          You didn&rsquo;t have to name it, and you don&rsquo;t need to. Send
          this over and we&rsquo;ll work it out with you.
        </p>
      )}

      <ul className="mm-quiz__result-chips">
        <li>
          <FiCalendar size={14} aria-hidden="true" />
          {result.availability}
        </li>
        <li>
          <FiGlobe size={14} aria-hidden="true" />
          {result.language}
        </li>
        <li>
          <FiUsers size={14} aria-hidden="true" />
          {result.forWho}
        </li>
      </ul>

      <div className="mm-quiz__result-actions">
        <Button className="px-4" onClick={onSend}>
          Send this to our team
          <FiArrowRight size={16} className="ms-2" aria-hidden="true" />
        </Button>
        <Button variant="link" className="mm-quiz__skip" onClick={onBrowse}>
          Browse conditions instead
        </Button>
      </div>

      <p className="mm-quiz__disclaimer">
        This is a starting point, not a diagnosis. Only a qualified clinician
        can give you one of those.
      </p>
    </div>
  );
}

/**
 * The 2-minute match quiz offered under the hero search.
 *
 * Six questions, one per screen, ending in a query rather than a verdict: the
 * result is the same {condition, availability, language, forWho} the search bar
 * produces, so "send this to our team" hands over to the enquiry form with
 * everything already filled in.
 *
 * Deliberately not scored. There is no engine here pretending to rank symptoms
 * — the answers become context a human reads, which is the only honest thing to
 * do with six multiple-choice questions about someone's mental health.
 */
export default function MatchQuiz({ show, onHide, onFinish }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showCrisis, setShowCrisis] = useState(false);
  const [crisisSeen, setCrisisSeen] = useState(false);
  const [done, setDone] = useState(false);

  // Start clean every time it opens. Someone reopening the quiz is redoing it,
  // not resuming a session they abandoned.
  useEffect(() => {
    if (!show) return;
    setStep(0);
    setAnswers({});
    setShowCrisis(false);
    setCrisisSeen(false);
    setDone(false);
  }, [show]);

  const question = QUESTIONS[step];
  const total = QUESTIONS.length;
  const result = useMemo(() => buildResult(answers), [answers]);
  const answered = question ? isComplete(answers, question) : false;

  const choose = (option) => {
    setAnswers((prev) => {
      if (question.type !== 'multi') return { ...prev, [question.id]: option.value };

      const current = prev[question.id] || [];
      if (current.includes(option.value)) {
        return { ...prev, [question.id]: current.filter((v) => v !== option.value) };
      }
      // Silently ignoring the fourth pick would read as a broken button, so the
      // limit is enforced by disabling the rest once it's reached.
      if (question.limit && current.length >= question.limit) return prev;
      return { ...prev, [question.id]: [...current, option.value] };
    });
  };

  const isChosen = (option) =>
    question.type === 'multi'
      ? (answers[question.id] || []).includes(option.value)
      : answers[question.id] === option.value;

  const atLimit =
    question?.type === 'multi' &&
    question.limit &&
    (answers[question.id] || []).length >= question.limit;

  const next = () => {
    // The crisis screen sits between this answer and the next question, and is
    // shown once — repeating it every time they step backwards and forwards
    // would turn a serious thing into an obstacle.
    if (
      !crisisSeen &&
      (answers.struggles || []).includes(CRISIS_VALUE)
    ) {
      setShowCrisis(true);
      setCrisisSeen(true);
      return;
    }
    if (step + 1 < total) setStep(step + 1);
    else setDone(true);
  };

  const back = () => {
    if (showCrisis) setShowCrisis(false);
    else if (step > 0) setStep(step - 1);
  };

  const progress = done ? 100 : Math.round((step / total) * 100);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      scrollable
      className="mm-quiz"
      aria-labelledby={TITLE_ID}
    >
      <Modal.Header closeButton closeLabel="Close">
        <div className="w-100">
          <Modal.Title className="mm-quiz__title" id={TITLE_ID}>
            The 2-minute match quiz
          </Modal.Title>
          <p className="mm-quiz__sub">
            Six questions. No account, no results page you have to decode &mdash;
            a real person reads what you send.
          </p>

          <div className="mm-quiz__progress">
            <div
              className="mm-quiz__progress-bar"
              style={{ width: `${progress}%` }}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Quiz progress"
            />
          </div>
          {!done && !showCrisis && (
            <p className="mm-quiz__count">
              Question {step + 1} of {total}
            </p>
          )}
        </div>
      </Modal.Header>

      <Modal.Body>
        {done ? (
          <Result
            result={result}
            onSend={() => onFinish?.(result)}
            onBrowse={() => {
              onHide?.();
              document
                .getElementById('categories')
                ?.scrollIntoView({ behavior: 'smooth' });
            }}
          />
        ) : showCrisis ? (
          <CrisisStep
            onContinue={() => {
              setShowCrisis(false);
              if (step + 1 < total) setStep(step + 1);
              else setDone(true);
            }}
          />
        ) : (
          <fieldset className="mm-quiz__step">
            <legend className="mm-quiz__prompt">{question.prompt}</legend>
            {question.help && <p className="mm-quiz__help">{question.help}</p>}

            <div className="mm-quiz__options">
              {question.options.map((option) => {
                const chosen = isChosen(option);
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`mm-quiz__option ${chosen ? 'mm-quiz__option--on' : ''} ${
                      option.value === CRISIS_VALUE ? 'mm-quiz__option--crisis' : ''
                    }`}
                    // Radio/checkbox semantics without the markup: these are
                    // buttons, so the pressed state has to be announced.
                    aria-pressed={chosen}
                    disabled={!chosen && atLimit}
                    onClick={() => choose(option)}
                  >
                    <span className="mm-quiz__tick" aria-hidden="true">
                      {chosen && <FiCheck size={13} />}
                    </span>
                    {option.label}
                  </button>
                );
              })}
            </div>
          </fieldset>
        )}
      </Modal.Body>

      {!done && !showCrisis && (
        <Modal.Footer className="mm-quiz__footer">
          <Button
            variant="link"
            className="mm-quiz__back"
            onClick={back}
            disabled={step === 0}
          >
            <FiArrowLeft size={15} className="me-1" aria-hidden="true" />
            Back
          </Button>
          <Button className="px-4" onClick={next} disabled={!answered}>
            {step + 1 === total ? 'See where to start' : 'Continue'}
            <FiArrowRight size={16} className="ms-2" aria-hidden="true" />
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}
